/*
  - This is supposed to match current prod app behavior, so visually it won't seem to match the rest of the UI.
*/
import React from "react";
import classes from "../../styles/CustomerResponsibilitiesModal.module.scss";
import classnames from "classnames";
import { Text } from "@suralink/samurai/ui";

type CustomerResponsibilitiesModalProps = {
  closeModal: () => void;
};

export default function CustomerResponsibilitiesModal({
  closeModal,
}: CustomerResponsibilitiesModalProps) {
  return (
    <div className={classes.customerResponsibilitiesModal}>
      <div className={classnames(classes.header)} onClick={closeModal}>
        <Text type="H2" className={classes.headerText}>
          Customer Responsibilities
        </Text>
        <Text
          type="H2"
          className={classnames(classes.headerText, classes.closeX)}
        >
          X
        </Text>
      </div>
      <div className={classes.content}>
        <div className={classes.centerContent}>
          <p className={classes.heading}>
            <span className={classnames(classes.mainHeaderText, classes.text)}>
              Customer Responsibilities
            </span>
          </p>
          <p className={classes.heading}>
            <span className={classnames(classes.text, classes.headerText)}>
              GENERAL
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink&rsquo;s secure file transfer with integrated requesting
              and workflow saves hours of time and eliminates miscommunication.
              Make requests, receive documents, and communicate all in one
              simple and organized place. Every person working on the engagement
              is looking at the same thing in real time. Both parties can upload
              files to be exchanged, toggle the status of requests, comment on
              individual requests back and forth, search and sort, and view all
              activity in chronological order.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              As a member of the Suralink community, users should be proactive
              in recognizing the value, sensitivity, and need to safeguard the
              information provided by the service.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              This document details Suralink user and customer responsibilities
              as they relate to acceptable usage of the Suralink service. It is
              the responsibility of Suralink users to familiarize themselves
              with the information and procedures below, and to comply with the
              service usage and notification requirements which are intended to
              maximize protection of Suralink users&rsquo; information.
            </span>
          </p>
          <p className={classnames(classes.heading)}>
            <span className={classnames(classes.text, classes.headerText)}>
              SAFEGUARDING DATA AND INFORMATION
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Access to the Suralink service requires a login ID and password.
              When an organization subscribes to the Suralink service, it is the
              clients responsibility to manage which end users should be given
              access. Clients should also determine when access should be taken
              away from end users.{" "}
            </span>
            <span className={classes.text}>
              Only valid account credentials{" "}
            </span>
            <span className={classes.text}>can be used.</span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              The Suralink service should always be considered sensitive and
              confidential by Suralink users. Users should follow information
              security best practices in ensuring access to their account
              credentials is appropriately limited, as well as ensuring that the
              information and functionality provided by the Suralink service is
              protected and restricted from unauthorized use.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink service users are responsible for maintaining the
              security and confidentiality of their user credentials (e.g.,
              Login ID and Password), and are responsible for all activities and
              uses performed under their account credentials whether authorized
              by them or not.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              By establishing user credentials and accessing the Suralink
              environment, end users of the Suralink service agree to comply
              with these requirements to safeguard assets and account
              information.
            </span>
          </p>
          <p className={classnames(classes.heading)}>
            <span className={classnames(classes.text, classes.headerText)}>
              CREDENTIAL MANAGEMENT
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Cloud-based services are accessible to the global Internet public,
              and as a result, great care must be exercised by Suralink service
              users in protecting their credentials against unauthorized access
              and use.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              By establishing user credentials and accessing the Suralink
              environment, service end users agree to proactively protect the
              security and confidentiality of their user credentials and never
              share service account credentials, disclose any passwords or user
              identifications to any unauthorized persons, or permit any
              unauthorized person to use or access Suralink accounts.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Any loss of control of passwords or user identifications could
              result in the loss of &quot;Personally Identifiable Information
              (PII)&quot; and the culpable account owner(s) may be liable for
              the actions taken under their service account credentials whether
              they authorized the activity or not.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              When establishing Suralink account credentials, end users are
              required to establish strong passwords following password strength
              and complexity best practices. Additionally, passwords should not
              be easily guessable. Two factor authentication is available for
              all accounts and is strongly recommended.
            </span>
          </p>
          <p className={classnames(classes.heading)}>
            <span className={classnames(classes.text, classes.headerText)}>
              REPORTING OPERATIONAL ISSUES
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              If Suralink users ever observe performance issues, problems, or
              service outages, users should contact Suralink immediately.
              Proactive reporting of these issues provides benefits for the
              entire Suralink community.
            </span>
          </p>
          <p className={classnames(classes.heading)}>
            <span className={classnames(classes.text, classes.headerText)}>
              SECURITY
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              By establishing Suralink account credentials or accessing the
              Suralink service, end users of the Suralink service agree to
              notify Suralink immediately of any security incident, including
              any suspected or confirmed breach of security. Users of the
              service agree to logout of the service immediately at the end of
              each session to provide further protection against unauthorized
              use.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink users should notify Suralink immediately if they observe
              any activity or communications that may indicate that the Suralink
              service or other Suralink accounts have been compromised.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink users should notify Suralink of any known or suspected
              security vulnerabilities. Suralink is dedicated to providing
              secure services to clients, and will prioritize and fix security
              vulnerabilities in accordance with the risk that they pose.
            </span>
          </p>
          <p className={classnames(classes.heading)}>
            <span className={classnames(classes.text, classes.headerText)}>
              QUESTIONS, CONCERNS, COMPLAINTS, &amp; OTHER FEEDBACK
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink users are encouraged to communicate any questions,
              concerns, complaints, or any other feedback related to the
              Suralink solution. To aid in proper understanding and appropriate
              response, these items should include as much detail as possible.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink encourages complainants to supply names and contact
              details and will not release this information except where
              required by law. Suralink will review all submitted complaints
              where sufficient detail has been provided but cannot commit to
              managing or resolving all complaints reported anonymously or for
              factors that are outside of Suralink control.
            </span>
          </p>
          <p className={classnames(classes.heading)}>
            <span className={classnames(classes.text, classes.headerText)}>
              SYSTEM REQUIREMENTS
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink is an internet browser based application. There are no
              plugins or programs to install to use Suralink.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classnames(classes.italic, classes.text)}>
              The following browsers are fully supported by Suralink:
            </span>
          </p>
          <ul className={classnames(classes.ul, classes.start)}>
            <li className={classes.li}>
              <span className={classes.text}>
                Internet Explorer &ndash; version 11 and higher
              </span>
            </li>
            <li className={classes.li}>
              <span className={classes.text}>
                Firefox - version 30 and higher
              </span>
            </li>
            <li className={classes.li}>
              <span className={classes.text}>
                Chrome - version 30 and higher
              </span>
            </li>
            <li className={classes.li}>
              <span className={classes.text}>
                Safari - version 7 and higher
              </span>
            </li>
            <li className={classes.li}>
              <span className={classes.text}>Edge - version 80 and higher</span>
            </li>
          </ul>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Other browsers or browser versions are not officially supported.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink is a modern web application that uses some of the newest
              capabilities of HTML5, CSS3, and JavaScript. Different browsers
              still interpret these in different ways. These differences could
              potentially cause severe bugs - like data not saving, or data
              becoming corrupted and overwriting good data.
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              We test and develop for the list of compatible browsers and block
              access to Suralink from specific incompatible browsers. This is in
              order to protect your data.
            </span>
          </p>
          <p className={classnames(classes.heading)}>
            <span className={classnames(classes.text, classes.headerText)}>
              CONTACT INFORMATION
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              To notify the appropriate Suralink incident response or support
              personnel, please use the in-app help or feedback systems, or use
              the contact information listed below:
            </span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              Suralink address and contact information:
            </span>
          </p>
          <p className={classes.addressText}>
            <span className={classes.text}>10 West Broadway, Suite 200</span>
          </p>
          <p className={classes.addressText}>
            <span className={classes.text}>Salt Lake City, UT 84101</span>
          </p>
          <p className={classes.addressText}>
            <span className={classes.text}>Email: </span>
            <a
              className={classnames(classes.link, classes.text)}
              href="mailto:support@suralink.com"
            >
              support@suralink.com
            </a>
          </p>
          <p className={classes.addressText}>
            <span className={classes.text}>Phone: 801-203-0002</span>
          </p>
          <p className={classes.paragraph}>
            <span className={classes.text}>
              If you do not agree with these terms,
            </span>
            <span className={classes.text}>&nbsp;</span>
            <span className={classes.text}>do not use this site.</span>
          </p>
          <br />
        </div>
      </div>
    </div>
  );
}
